/**
 * API 데이터 변환 유틸리티
 * #42: API 타입 → 프론트엔드 타입 변환 함수
 */
import type { Store as ApiStore, StoreDetail, MenuStoreDetail, CustomerCompany, EntityStatus } from './types';
import type { Store as LocalStore } from '@/data';
import type { Company } from '@/types';
import placeholderStore from '@/assets/placeholder-store.webp';

/**
 * 매장 영업 여부 판정
 * temporarily_open → 항상 영업, approved → is_open_now 기반, 나머지 → 비영업
 */
export function isStoreOpen(status: EntityStatus, isOpenNow?: boolean): boolean {
  return status === 'temporarily_open' || (status === 'approved' && (isOpenNow ?? false));
}

/**
 * 백엔드 EntityStatus + is_open_now를 로컬 표시 상태로 변환
 */
function getLocalStatus(status: EntityStatus, isOpenNow?: boolean): '영업중' | '준비중' | '마감' {
  if (status === 'temporarily_closed') return '준비중';
  return isStoreOpen(status, isOpenNow) ? '영업중' : '마감';
}

/**
 * API 영업시간을 로컬 형식으로 변환
 *
 * #651 / admin#1849: 요일별 정기 휴무(is_closed)는 휴무 요일도 open/close 시간이
 * 보존되어 내려오므로 (토글 해제 시 복원용) 시간 존재 여부보다 먼저 판정한다.
 * is_closed 부재 (구버전 응답) 시 기존 시간 기반 분기 그대로 — additive 무회귀.
 * 표시 전용 — 주문 가능 판정은 is_open_now 서버 권위 (본 함수 비관여).
 */
function transformHours(hours: ApiStore['hours'] | undefined): LocalStore['hours'] {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  const result = {} as LocalStore['hours'];

  for (const day of days) {
    const apiHours = hours?.[day];
    if (apiHours?.is_closed) {
      // 정기 휴무 요일 — 시간이 보존돼 있어도 휴무로 표시
      result[day] = {
        open: '',
        close: '',
        isClosed: true,
      };
    } else if (apiHours && apiHours.open && apiHours.close) {
      result[day] = {
        open: apiHours.open,
        close: apiHours.close,
      };
    } else {
      result[day] = {
        open: '',
        close: '',
        isClosed: true,
      };
    }
  }
  return result;
}

/**
 * API Store를 로컬 Store 형식으로 변환
 * #194: MenuStoreDetail 타입 지원 추가 (메뉴 API 응답의 매장 정보)
 * #907: tax_rate, currency 필드 변환 추가 (API 퍼센트 → 프론트엔드 비율)
 */
export function transformApiStoreToLocal(apiStore: ApiStore | StoreDetail | MenuStoreDetail): LocalStore {
  // StoreDetail과 MenuStoreDetail에만 organization과 brand 필드가 존재
  const hasOrganization = 'organization' in apiStore;
  const hasBrand = 'brand' in apiStore;

  return {
    id: String(apiStore.id),
    nameKey: `api_store_${apiStore.id}`,
    name: apiStore.name,
    status: getLocalStatus(apiStore.status, apiStore.is_open_now),
    prep_time: null,
    address: apiStore.address,
    imageUrl: apiStore.image_url || placeholderStore,
    hours: transformHours(apiStore.hours),
    // tax_rate: API는 퍼센트(16), 프론트는 비율(0.16)
    taxRate: apiStore.tax_rate != null ? apiStore.tax_rate / 100 : undefined,
    currency: apiStore.currency ?? undefined,
    // #1128: BE PR #1129로 노출된 매장 IANA 타임존
    timezone: apiStore.timezone ?? undefined,
    // organization.id → organizationId 매핑
    organizationId: hasOrganization && apiStore.organization?.id
      ? String(apiStore.organization.id)
      : null,
    // brand.id → brandId 매핑
    brandId: hasBrand && apiStore.brand?.id
      ? String(apiStore.brand.id)
      : null,
    // Q7 박제 옵션 C (menu#347 / admin#1298) — BE 응답 그대로 전달, FE 재해석 금지.
    // P1-1 회귀 게이트: undefined (hours relation 미로드) 와 null (로드됐으나 사유 없음)
    // 의미를 hasQ7Hours flag 로 보존. transformer 의 `?? null` 폴백이 silent
    // regression (outside_hours 누락) 을 만들지 않도록 키 존재 여부로 판정.
    apiStatus: apiStore.status,
    isOpenNow: apiStore.is_open_now,
    closureReason: apiStore.closure_reason ?? null,
    nextAvailableAt: apiStore.next_available_at ?? null,
    // codex 2R P1 시정 — OR → closure_reason 단독.
    // closure_reason 이 getStoreOpenLabel outside_hours 분기 기준이므로,
    // next_available_at 단독 partial payload 가 hasQ7Payload 게이트 통과 후
    // generic closed 라벨로 silent fallback 하는 회귀 차단.
    hasQ7Hours: 'closure_reason' in apiStore,
  };
}

/**
 * API CustomerCompany를 프론트엔드 Company 타입으로 변환
 * snake_case → camelCase 변환
 * #42: Company 타입 통일
 */
export function transformApiCompanyToLocal(apiCompany: CustomerCompany): Company {
  return {
    id: String(apiCompany.id),
    name: apiCompany.name,
    rfc: apiCompany.rfc,
    email: apiCompany.email,
    phone: apiCompany.phone,
    address: apiCompany.address,
    city: apiCompany.city,
    state: apiCompany.state,
    postalCode: apiCompany.postal_code,
    taxRegime: apiCompany.tax_regime,
    cfdiUse: apiCompany.cfdi_use,
    isDefault: apiCompany.is_default,
    createdAt: apiCompany.created_at,
  };
}

/**
 * 프론트엔드 Company를 API CustomerCompany 타입으로 변환
 * camelCase → snake_case 변환
 * #42: Company 타입 통일
 */
export function transformLocalCompanyToApi(company: Company): Omit<CustomerCompany, 'id' | 'created_at'> {
  return {
    name: company.name,
    rfc: company.rfc,
    email: company.email,
    phone: company.phone,
    address: company.address,
    city: company.city,
    state: company.state,
    postal_code: company.postalCode,
    tax_regime: company.taxRegime,
    cfdi_use: company.cfdiUse,
    is_default: company.isDefault,
  };
}
